// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { MatTabsModule } from '@angular/material/tabs';
import { finalize } from 'rxjs/operators';
import { ButtonsComponent } from "../../shared/buttons/buttons.component";
import { HceService } from '../../services/hce/hce.service';
import { Analysis } from '../../interfaces/analysis';
import { MessageModalService } from '../../services/message-modal/message-modal.service';
import { catchError, filter, forkJoin, map, of, switchMap, tap, throwError, Subscription, take, shareReplay } from 'rxjs';
import { HceResultsSummaryViewComponent } from "./hce-results-summary-view/hce-results-summary-view.component";
import { HceSvListViewComponent } from './hce-sv-list-view/hce-sv-list-view.component';
import { MatDialog } from '@angular/material/dialog';
import { HceAnnotationModalComponent, HceAnnotationModalData, } from './hce-annotation-modal/hce-annotation-modal.component';
import { HceSvFilterModalComponent, HceSvFilterModalData } from './hce-sv-filter-modal/hce-sv-filter-modal.component';
import { HceSvFilter } from '../../interfaces/hce/hce-sv-filter';
import { HceAnnotation } from '../../interfaces/hce/hce-annotation';
import { HceParserService } from '../../services/hce/hce-parser/hce-parser.service';
import { HceRefGeneListViewComponent } from './hce-ref-gene-list-view/hce-ref-gene-list-view.component';
import { HceStatsViewComponent } from "./hce-stats-view/hce-stats-view.component";
import { HceTabControlService } from '../../services/hce/hce-tab-control/hce-tab-control.service';
import { SvResponse } from '../../interfaces/hce/hce-sv';
import { HceSvFilterService } from '../../services/hce/hce-sv-filter/hce-sv-filter.service'
import { HceLoadingOverlayService } from '../../services/hce/hce-loading-overlay/hce-loading-overlay.service';
import { HceReportsModalComponent, ReportFile, HceReportsModalData } from './hce-reports-modal/hce-reports-modal.component';
import { normalizeHceSvFilter } from '../../shared/hce/normalizers/hce-sv-filter/hce-sv-filter.normalizer';
import { DEFAULT_POSITION_MAX } from '../../shared/constants/hce/hce-sv-filter-constants';
import { isSvFilterSvType } from '../../shared/hce/utils/hce-sv-filter/hce-sv-filter.util';
import { countHceSvFilter } from '../../shared/hce/utils/hce-sv-filter/hce-sv-filter.util';

const TAB_INDEX_MAP: Record<string, number> = {
  stats: 0,
  summary: 1,
  sv: 2,
  ref: 3,
};

@Component({
  selector: 'app-hce',
  standalone: true,
  imports: [
    CommonModule,
    MatTabsModule,
    ButtonsComponent,
    HceResultsSummaryViewComponent,
    HceRefGeneListViewComponent,
    HceStatsViewComponent,
    HceSvListViewComponent
  ],
  providers: [
    HceTabControlService,
  ],
  templateUrl: './hce.component.html',
  styleUrls: ['./hce.component.css'],
})
export class HceComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private readonly svFilterSvc = inject(HceSvFilterService);

  private readonly SV_FILTER_STORAGE_KEY = 'hceSvFilter';

  analysisId!: number;
  analysis: Analysis | null = null;
  selectedIndex = 0;
  private sub = new Subscription();
  isDataLoading = true;
  isBuildingOverlap = false;
  hasError = false;

  annotationByFile: Record<string, HceAnnotation[]> = {};
  filterAnnotationByFile: Record<string, HceAnnotation[]> = {};
  selectedAnnotationFiles: string[] = [];
  initialAnnotationParams: string[] = [];

  refNameMap: Record<string, string> = {};
  filterRefNameMap = this.refNameMap;
  svFilter: HceSvFilter = {};
  filtered: boolean = false;
  private reportFilesCache: ReportFile[] | null = null;

  baseSV: SvResponse = {
    svs: {},
    version: "",
    hce_version: "",
  };

  filterSV: SvResponse = {
    svs: {},
    version: "",
    hce_version: "",
  };

  get selectedAnnotationByFile(): Record<string, HceAnnotation[]> {
    return Object.fromEntries(
      Array.from(this.selectedAnnotationFiles).map(f => [f, this.annotationByFile[f] ?? []])
    );
  }

  get selectedFilterAnnotationByFile(): Record<string, HceAnnotation[]> {
    return Object.fromEntries(
      Array.from(this.selectedAnnotationFiles).map(f => [f, this.filterAnnotationByFile[f] ?? []])
    );
  }

  get isBusy(): boolean {
    return this.isDataLoading || this.isBuildingOverlap;
  }

  constructor(
    private hceService: HceService,
    private parser: HceParserService,
    private messageModal: MessageModalService,
    private dialog: MatDialog,
    private tabControl: HceTabControlService,
    private loadingOverlay: HceLoadingOverlayService,
  ) {
    this.route.paramMap.pipe(
      map(params => Number(params.get('analysisId'))),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(analysisId => (this.analysisId = analysisId));

    this.route.queryParamMap.pipe(
      take(1),
      map(q => {
        const tab = q.get('tab');
        const idx = tab ? TAB_INDEX_MAP[tab.toLowerCase()] : null;

        return {
          idx: Number.isInteger(idx) ? idx : null,
          annotations: q.getAll('annotations'),
        };
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(({ idx, annotations }) => {
      if (idx !== null) {
        this.selectedIndex = idx
      }
      this.initialAnnotationParams = annotations;
    });

    this.sub.add(
      this.tabControl.tabChange$.subscribe(idx => this.selectedIndex = idx)
    );
  }
  ngOnInit(): void {
    this.loadingOverlay.open();

    sessionStorage.removeItem('hceSvFilter');

    const analysis$ = this.hceService.getAnalysis(this.analysisId).pipe(
      map(response => {
        if (!response || response.success === false) {
          throw new Error(response?.message ?? 'Failed to fetch analysis.');
        }
        return response.data as Analysis | undefined;
      }),
      filter((analysis): analysis is Analysis => !!analysis),
      tap(analysis => this.analysis = analysis),
      shareReplay(1)
    );

    analysis$.pipe(
      switchMap(ana =>
        this.hceService.getReportIncludeFlags(ana).pipe(
          switchMap(flags =>
            this.hceService.getReportFilesIndex(ana, {
              includeRefs: true,
              includeAlignment: flags.includeAlignment,
              includeUsedUnusedMaps: flags.includeUsedUnusedMaps,
              includeReadsMapsZip: flags.includeReadsMapsZip,
              includeMapContigs: flags.includeMapContigs,
              includeMapReads: flags.includeMapReads,
            })
          )
        )
      ),
      catchError(err => {
        console.error('[Reports] index build failed:', err);
        return of<ReportFile[]>([]);
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(list => {
      this.reportFilesCache = list;
    });

    const sv$ = analysis$.pipe(
      switchMap(analysis => this.hceService.getSvJson(analysis.AnalysisID).pipe(
        map(sv => sv ?? {}),
        catchError(err => {
          console.error(err);
          return throwError(() => new Error('Failed to fetch sv_json.'));
        }),
      )),
    );

    const refNameMap$ = analysis$.pipe(
      switchMap(analysis => this.hceService.getRefsNameMapping(analysis.PanelID).pipe(
        map(refNameMap => refNameMap ?? {}),
        catchError(err => {
          console.error(err);
          return throwError(() => new Error('Failed to fetch refs_name_mapping.'));
        }),
      )),
    );

    const annotations$ = this.hceService.getAnnotationFile().pipe(
      catchError(err => {
        console.error(err);
        return throwError(() => new Error('Failed to fetch annotation.'));
      }),
    );

    forkJoin({
      annotations: annotations$,
      refNameMap: refNameMap$,
      sv: sv$,
    }).pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => {
        this.loadingOverlay.close();
        this.isDataLoading = false;
      }),
      catchError(err => {
        console.error(err);
        this.hasError = true;
        this.messageModal.openModal(
          $localize`Error`,
          $localize`Failed to load analysis results.`
        );
        return of(null)
      })
    ).subscribe(result => {
      if (!result) return;
      const { annotations, refNameMap, sv } = result;
      const entries = Object.entries(annotations);
      const options: Intl.CollatorOptions = { numeric: true, sensitivity: 'base' };
      entries.sort(([a], [b]) => a.localeCompare(b, undefined, options));
      const next: Record<string, HceAnnotation[]> = {};
      for (const [filename, text] of entries) {
        next[filename] = this.parser.annotation.parse(filename, text, refNameMap);
      }
      this.annotationByFile = structuredClone(next);
      this.refNameMap = structuredClone(refNameMap);
      this.baseSV = structuredClone(sv);
      this.applyDefauiltFilter();
      this.applyInitialAnnotationsIfNeeded();
    });
  }

  onAnnotationClick(): void {
    if (this.analysis) {
      this.dialog.open<HceAnnotationModalComponent, HceAnnotationModalData, string[]>(
        HceAnnotationModalComponent,
        {
          maxWidth: 'none',
          width: '30rem',
          autoFocus: 'dialog',
          restoreFocus: true,
          data: {
            annotationFiles: Object.keys(this.annotationByFile),
            selectedFiles: this.selectedAnnotationFiles,
            annotUsedInFilter: this.getUsedAnnotationFiles(),
          } satisfies HceAnnotationModalData,
        }
      )
      .afterClosed()
      .pipe(
        filter((files): files is string[] => Array.isArray(files)),
      )
      .subscribe(async files => {
        this.selectedAnnotationFiles = [...files];
        this.addSettingsCount('Annotation', this.selectedAnnotationFiles.length);
        try {
          if (this.selectedAnnotationFiles.length > 0) {
            this.isBuildingOverlap = true;
            this.loadingOverlay.open();
            await this.svFilterSvc.buildOverlapCache(
              this.baseSV.svs,
              this.refNameMap,
              this.selectedAnnotationByFile,
              200
            );
          } else {
            this.svFilterSvc.clearOverlapCache();
          }
          this.recomputeFilteredView();
        } finally {
          if (this.isBuildingOverlap) {
            this.loadingOverlay.close();
            this.isBuildingOverlap = false;
          }
        }
      });
    }
  }

  onReportsClick(): void {
    if (!this.analysis) return;

    const sampleName = this.analysis?.SampleName ?? '-';
    const sampleId = this.analysis?.SampleID ?? '-';
    const ORDER = [
      'Map Reads',
      'Map Contigs',
      'Input reads maps(zip)',
      'Reference(SV)',
      'Coverage Txt',
      'SMRM Stats',
      'Assembly Stats',
      'Assembly reads-maps usage',
      'Reference Gene',
      'RefGene(exon)',
      'Alignment JSON',
      'Assemble JSON',
      'Multihits JSON',
      'SV JSON',
      'Nabsys format assembly files',
      'Enhanced Ref.gene',
      'Assembly Params',
      'SV Params',
      'Aligner Params(con2ref)',
      'Aligner Params(map2ref)',
    ] as const;

    (document.activeElement as HTMLElement | null)?.blur();
    const applyCachedOrFetched = (records: ReportFile[]) => {
      const presentTypes = Array.from(new Set(records.map(r => r.fileType)));
      const unexpected = presentTypes.filter(t => !ORDER.includes(t as any));
      const fileTypeOptions = [...ORDER, ...unexpected];

      // Run heavy processing in the next frame after the modal is rendered
      requestAnimationFrame(() => {
        dialogRef.componentInstance?.setRecords(records, fileTypeOptions);
      });
    };    

    const dialogRef = this.dialog.open(HceReportsModalComponent, {
      maxWidth: 'none',
      width: '60vw',
      maxHeight: '80vh',
      autoFocus: 'dialog',
      restoreFocus: true,
      data: {
        analysisId: this.analysisId,
        records: [],
        fileTypeOptions: [],
        sampleName,
        sampleId,
        loading: true,
      } satisfies HceReportsModalData,
    });
    
    // If already acquired, after opening, flush it in the next frame.
    if (this.reportFilesCache !== null) {
      dialogRef.afterOpened().pipe(take(1)).subscribe(() => {
        applyCachedOrFetched(this.reportFilesCache!);
      });
      return;
    }

    // If not obtained, first open it and then fetch.
    const sub = this.hceService.getReportIncludeFlags(this.analysis).pipe(
      switchMap(flags =>
        this.hceService.getReportFilesIndex(this.analysis!, {
          includeRefs: true,
          includeAlignment: flags.includeAlignment,
          includeUsedUnusedMaps: flags.includeUsedUnusedMaps,
          includeReadsMapsZip: flags.includeReadsMapsZip,
          includeMapContigs: flags.includeMapContigs,
          includeMapReads: flags.includeMapReads,
        })
      ),
      take(1),
    ).subscribe({
      next: list => {
        this.reportFilesCache = list;
        applyCachedOrFetched(list);
      },
      error: err => {
        console.error('[Reports] fallback index failed:', err);
        requestAnimationFrame(() => {
          dialogRef.componentInstance?.setRecords([], []);
        });
      }
    });

    dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
      sub.unsubscribe();
    });
  }

  onFilterClick(): void {
    if (this.analysis) {
      this.dialog.open<HceSvFilterModalComponent, HceSvFilterModalData, { filter: HceSvFilter; count: number }>(
        HceSvFilterModalComponent,
        {
          maxWidth: 'none',
          width: '1300px',
          height: '80vh',
          autoFocus: 'dialog',
          restoreFocus: true,
          disableClose: true,
          data: {
            svFilter: this.svFilter,
            annotationFiles: this.selectedAnnotationFiles,
            refNameMap: this.refNameMap,
          } satisfies HceSvFilterModalData,
        }
      )
      .afterClosed()
      .subscribe(result => {
        if (!result) return;
        const { filter, count } = result;
        this.svFilter = filter ?? {};
        this.addSettingsCount('Filter', count);
        this.saveSvFilterToSession(this.svFilter);
        this.recomputeFilteredView();
        this.filtered = count !== 0;
      });
    }
  }

  addSettingsCount(name: string, count: number): void {
    const btn = document.querySelector(`app-buttons[name=${name}] button`);
    if (!(btn instanceof HTMLButtonElement)) return;
    const n = Number(btn.innerText);
    if (n == count) return;
    const el = btn.querySelector('.settings-count');
    if (el instanceof HTMLSpanElement) {
      btn.removeChild(el);
    }
    if (count > 0) {
      const el = document.createElement('span');
      el.classList.add('settings-count');
      el.innerText = String(count);
      btn.appendChild(el);
    }
  }
  ngOnDestroy() {
    this.sub.unsubscribe();
    sessionStorage.removeItem('hceSvFilter');
  }

  private setSessionStorageSafe(key: string, value: unknown): void {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch {}
  }

  private saveSvFilterToSession(filter: HceSvFilter | undefined): void {
    const normalized = normalizeHceSvFilter(filter ?? {});
    this.setSessionStorageSafe(this.SV_FILTER_STORAGE_KEY, normalized);
  }

  private applyDefauiltFilter(): void {
    this.svFilter = this.svFilterSvc.getDefaultFilter();
    this.svFilter.screenType = "SV";
    const count = countHceSvFilter(this.svFilter);
    this.filtered = count > 0;
    
    this.addSettingsCount('Filter', count);
    this.saveSvFilterToSession(this.svFilter);
    this.recomputeFilteredView();
  }

  private async applyInitialAnnotationsIfNeeded(): Promise<void> {
    if (!this.initialAnnotationParams.length) return;

    const validFiles = this.initialAnnotationParams.filter(
      f => this.annotationByFile[f]
    );

    if (validFiles.length === 0) return;

    this.selectedAnnotationFiles = [...validFiles];
    this.addSettingsCount('Annotation', this.selectedAnnotationFiles.length);

    try {
      this.isBuildingOverlap = true;
      this.loadingOverlay.open();

      await this.svFilterSvc.buildOverlapCache(
        this.baseSV.svs,
        this.refNameMap,
        this.selectedAnnotationByFile,
        200
      );

      this.recomputeFilteredView();
    } finally {
      this.loadingOverlay.close();
      this.isBuildingOverlap = false;
    }
  }

  // Recreate filterSV based on baseSV/svFilter/selectedAnnotationByFile
  private recomputeFilteredView(): void {
    const filtered = this.svFilterSvc.filterSV(
      this.baseSV.svs,
      this.refNameMap,
      this.svFilter ?? {},
      this.selectedAnnotationByFile,
    );

    this.filterSV = {
      ...this.baseSV,
      svs: filtered,
    };

    this.filterAnnotationByFile = this.svFilterSvc.filterAnnotation(
      this.annotationByFile,
      this.refNameMap,
      this.svFilter ?? {},
      this.selectedAnnotationFiles,
    );

    this.filterRefNameMap = this.svFilterSvc.filterChrList(
      this.refNameMap,
      this.svFilter?.physicalPosition?.map(item => Number(item.chr))
    );
  }

  private getUsedAnnotationFiles(): string[] {
    const match = this.svFilter?.matchAnnotation ?? [];
    return Array.from(new Set(match.map(m => m.annotation)));
  }

  onSvStatsCellFilter(e: { chr?: string | number; svType?: string }): void {
    let next: HceSvFilter = {};
    let count = 0;

    // CHR quick filter
    if (e.chr && String(e.chr).toUpperCase() !== 'ALL') {
      next.physicalPosition = [{
        chr: String(e.chr),
        posStart: 1,
        posEnd: DEFAULT_POSITION_MAX,
        posBp: 1,
      }];
      count++;
    }

    // SV Type filter
    if (isSvFilterSvType(e.svType)) {
      next.svType = [e.svType];
      count++;
    }

    // apply filter
    this.svFilter = next;
    this.filtered = count > 0;

    this.addSettingsCount('Filter', count);
    this.saveSvFilterToSession(this.svFilter);
    this.recomputeFilteredView();

    this.tabControl.requestChange(2);
  }

  onAssemblyStatsCellFilter(e: { chr?: string | number }): void {
    let next: HceSvFilter = {};
    let count = 0;

    // CHR quick filter
    if (e.chr && String(e.chr).toUpperCase() !== 'ALL') {
      next.physicalPosition = [{
        chr: String(e.chr),
        posStart: 1,
        posEnd: DEFAULT_POSITION_MAX,
        posBp: 1,
      }];
      count = 1;
    }

    // apply filter
    this.svFilter = next;
    this.filtered = count > 0;

    this.addSettingsCount('Filter', count);
    this.saveSvFilterToSession(this.svFilter);
    this.recomputeFilteredView();

    this.tabControl.requestChange(2);
  }
}
